import {
  ControllerFlowAPI,
  ControllerParams,
  CreateControllerFn,
} from '@wix/yoshi-flow-editor';
import { Plan } from '@wix/ambassador-checkout-server/types';
import { ServiceOptionsAndVariants } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { Booking } from '@wix/bookings-checkout-api';
import { FlowElements } from './Hooks/useFlow';
import { EmptyStateType } from './ViewModel/emptyStateViewModel/emptyStateViewModel';
import { CalendarStatus } from './ViewModel/widgetViewModel/widgetViewModel';
import {
  DialogState,
  DialogType,
} from './ViewModel/dialogViewModel/dialogViewModel';
import { SelectedBookingPreference } from '../../utils/bookingPreferences/bookingPreferences';
import {
  CalendarErrors,
  FilterOptions,
  Preset,
  SelectedVariantOptions,
  SlotsStatus,
  TriggeredByOptions,
} from '../../types/types';
import calendarSettingsParams from './settingsParams';
import { isCalendarPage } from '../../utils/presets';
import type { ServicesPreferencesModalData } from '@wix/bookings-services-preferences-modal/types';
import {
  getServiceSlug,
  CALENDAR_PAGE_URL_PATH_PARAM,
  ReservedSlug,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  TimeSlot,
  ListAvailabilityTimeSlotsResponse,
} from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { PreferenceToDisplayMapping } from '@wix/bookings-services-preferences-modal/utils';

export type TFunction = (
  key: string | string[],
  options?: Record<string, any>,
  defaultValue?: string,
) => string;

export type CalendarState = {
  calendarStatus: CalendarStatus;
  slotsStatus: SlotsStatus;
  servicesInView: Service[];
  selectedDate?: string;
  selectedDateTrigger?: TriggeredByOptions;
  selectedTimezone?: string;
  selectedRange?: {
    from: string;
    to: string;
  };
  selectedTime?: string;
  selectableSlotsAtSelectedTime?: TimeSlot[];
  availableSlots?: ListAvailabilityTimeSlotsResponse;
  availableSlotsPerDay?: ListAvailabilityTimeSlotsResponse;
  selectedBookingPreferences: SelectedBookingPreference[];
  calendarErrors: CalendarErrors[];
  rescheduleBookingDetails?: Booking;
  dialog?: {
    type: DialogType;
    state: DialogState;
  };
  filterOptions: FilterOptions;
  focusedElement?: FlowElements;
  initialErrors: EmptyStateType[];
  purchasedPricingPlans: Plan[];
  isUserLoggedIn: boolean;
  selectedVariantsOptions: SelectedVariantOptions[];
  serviceVariantsMap: Record<string, ServiceOptionsAndVariants>;
  servicesPreferencesModalData?: ServicesPreferencesModalData;
  appointmentPreferencesDisplay?: PreferenceToDisplayMapping;
  isPreferencesModalPartOfTheFlow: boolean;
};

export const createControllerFactory = (
  preset: Preset,
  settingsParams: any,
) => {
  const createController: CreateControllerFn = async (
    controllerParams: ControllerParams,
  ) => {
    if (
      !controllerParams.flowAPI.experiments.enabled(
        'specs.bookings.migrateUoUAvailability2',
      )
    ) {
      // Dynamically import the old implementation to reduce bundle size
      const { createControllerFactory: deprecatedCreateControllerFactory } =
        await import('../../BookingCalendarDeprecated/controller');
      return deprecatedCreateControllerFactory(
        preset,
        settingsParams,
      )(controllerParams);
    }

    const { createControllerFactory: newCreateControllerFactory } =
      await import('./createControllerFactory');
    return newCreateControllerFactory(preset, settingsParams)(controllerParams);
  };

  return createController;
};

export default createControllerFactory(
  Preset.CALENDAR_PAGE,
  calendarSettingsParams,
);
